import {
  Checkbox,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TextWithTooltipOnClick, {
  TextWithTooltipOnClickSize,
} from 'src/components/TextWithTooltipOnClick/TextWithTooltipOnClick'
import { UserStatus } from 'src/enums'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { signinUrl } from 'src/navigation/urls'
import {
  selectMfaSentFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

/**
 * @deprecated Use VerifyEmailModal and VerifyPhoneModal with CodeInput in
 * src/component-modal/ instead. This component is retained for the CSP signin flow.
 */
interface VerifyFormProps {
  className?: string
  email: string
  password?: string
  verificationResent: boolean
  isVerifyingUser: boolean
  title: React.ReactNode | string
  subtitle?: React.ReactNode
  wrongEmailText?: React.ReactElement
  hideWrongEmailText?: boolean
  buttonText: React.ReactNode | string
  verifyTopLevelError?: string
  resendVerificationTopLevelError?: string
  allowRememberMyDevice?: boolean
  id?: string
  setStatus: (val?: UserStatus) => void
  postResendVerification?: (email: string) => void
  postCognitoResendMfaCode?: (
    email: string,
    password?: string,
    newCode?: boolean,
  ) => void
  postVerification?: (code: string) => void
  confirmCognitoSignin?: (code: string, rememberDevice?: boolean) => void
  acceptOrgInvite?: boolean
  verifyPhoneNumberPopup?: boolean
  'data-testid'?: string
}

/**
 * @deprecated Use VerifyEmailModal and VerifyPhoneModal with CodeInput in
 * src/component-modal/ instead. This component is retained for the CSP signin flow.
 */
const VerifyForm: React.FC<VerifyFormProps> = (props) => {
  const translate = useTranslate()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const location = useSelector(selectLocationFromState)
  const mfaCodeEmailSent = useSelector(selectMfaSentFromState)
  const { analytics } = useAnalyticsContext()

  const [verificationCode, setVerificationCode] = useState('')
  const [rememberMyDevice, setRememberMyDevice] = useState(false)
  const [showCountdownError, setShowCountdownError] = useState(false)
  const [sendNewCodeClicked, setSendNewCodeClicked] = useState(false)

  const cannotSendNewCode =
    showCountdownError && mfaCodeEmailSent
      ? translate(
          'We cannot send you a new code at this time. Please try again later.',
        )
      : undefined

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    if (props.verifyPhoneNumberPopup) {
      analytics?.track(
        location.pathname.includes(signinUrl())
          ? 'verify phone - login'
          : 'verify phone - sign up',
      )
    } else if (props.allowRememberMyDevice) {
      analytics?.track('verify email - login', {
        remember_device_checkbox: rememberMyDevice,
        verification_code: verificationCode,
      })
    } else {
      analytics?.track(
        location.pathname.includes(signinUrl())
          ? 'verify email - login'
          : 'verify email - signup',
      )
    }

    props.postVerification && props.postVerification(verificationCode)
    props.confirmCognitoSignin &&
      props.confirmCognitoSignin(verificationCode, rememberMyDevice)
  }

  const sendNewCode = (): void => {
    setSendNewCodeClicked(true)
    if (props.verifyPhoneNumberPopup) {
      analytics?.track('phone verification - send new code')
    } else {
      analytics?.track('email verification - send new code')
    }
    if (!mfaCodeEmailSent) {
      props.postResendVerification && props.postResendVerification(props.email)
      props.postCognitoResendMfaCode &&
        props.postCognitoResendMfaCode(props.email, props.password, true)
      setShowCountdownError(false)
    } else {
      setShowCountdownError(true)
    }
  }

  useEffect((): void => {
    if (!props.allowRememberMyDevice) {
      analytics?.page(AnalyticsPageName.VerifyEmailSignUp, {
        screen: calculatedBackgroundLocation.pathname,
      })
    }
  }, [])

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
      `}
      className={props.className}
      data-testid={props['data-testid']}
      onSubmit={onSubmit}
    >
      <span
        id={props.id ? props.id : 'verifyFormHeader'}
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {props.title}
      </span>
      {props.subtitle && (
        <p
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          {props.subtitle}
        </p>
      )}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin: 16px 0;
        `}
      >
        <input
          css={css`
            border-radius: 6px;
            height: 70px;
            border: 1px solid ${styleConstants.colors.gray_medium};
            padding: 20px;
            caret-color: ${themeConstants.colors.cta};
            ${getFontCss(
              24,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
            text-align: center;
            background-color: ${styleConstants.colors.gray_lightest};

            &::placeholder {
              opacity: 0.5;
              ${getFontCss(
                24,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            }

            &:focus,
            &:hover {
              background-color: ${styleConstants.colors.white};
              border: 1px solid ${themeConstants.colors.cta};
            }
          `}
          value={verificationCode}
          placeholder={translateSingleString(locale, 'Enter verification code')}
          data-testid="codeInput"
          onChange={(e): void => {
            setVerificationCode(e.target.value)
          }}
        />
        {props.verifyTopLevelError && (
          <ErrorList errors={[props.verifyTopLevelError]} />
        )}
      </div>
      <Button
        id={props.id ? props.id + 'Btn' : 'verifyFormBtn'}
        type={ButtonTypes.submit}
        design={ButtonDesigns.designCta}
        text={props.buttonText}
        loading={props.isVerifyingUser}
        disabled={props.isVerifyingUser || verificationCode.length === 0}
        data-testid="verifyBtn"
        big
      />
      {props.allowRememberMyDevice && (
        <Checkbox
          css={css`
            align-self: center;
          `}
          label={
            <span
              css={css`
                color: ${styleConstants.colors.gray_darkest};
              `}
            >
              {translate('Remember this device')}
            </span>
          }
          isSelected={rememberMyDevice}
          onClick={(): void => {
            setRememberMyDevice((val) => !val)
          }}
        />
      )}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-self: center;
          align-items: center;
        `}
      >
        <TextWithTooltipOnClick
          text={translate('Send new code')}
          tooltipBody={translate('New code sent!')}
          onClick={sendNewCode}
          delayTrigger
          triggerNow={props.verificationResent && sendNewCodeClicked}
          size={TextWithTooltipOnClickSize.Medium}
        />
        {(props.resendVerificationTopLevelError || cannotSendNewCode) && (
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.red,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {props.resendVerificationTopLevelError || cannotSendNewCode}
          </span>
        )}
      </div>
      {!props.hideWrongEmailText && (
        <div
          css={css`
            align-self: center;
            margin-top: 16px;
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          {props.wrongEmailText
            ? props.wrongEmailText
            : translate('Return to the ${login} screen.', {
                login: (
                  <HoverableLink
                    underline
                    onClick={(): void => {
                      props.setStatus()
                    }}
                    ariaLabelSource={'Login'}
                  >
                    {translate('Login')}
                  </HoverableLink>
                ),
              })}
        </div>
      )}
    </form>
  )
}

export default VerifyForm
